/**
 * Company Filters Component
 * Inline filter bar: filters stay visible and apply live (no popover).
 */

import React from 'react';
import { InlineFilterBar, InlineMultiSelect } from '../../../components/common';
import { useFilterState } from '../../../hooks/useFilterState';
import { BRAZILIAN_STATES } from '../../../types/common';
import {
  CompanyStatus,
  CompanyType,
  ModuleType,
  COMPANY_STATUS_LABELS,
  COMPANY_TYPE_LABELS,
  MODULE_LABELS,
} from '../../../types/company';

// Types
interface CompanyFiltersData {
  searchTerm: string;
  status: string[];
  states: string[];
  companyTypes: string[];
  modules: string[];
}

interface CompanyFiltersProps {
  onFilter: (filters: CompanyFiltersData) => void;
  loading?: boolean;
  total?: number;
}

// Options
const STATUS_OPTIONS = Object.values(CompanyStatus).map((s) => ({
  value: s,
  label: COMPANY_STATUS_LABELS[s],
}));
const TYPE_OPTIONS = Object.values(CompanyType).map((t) => ({
  value: t,
  label: COMPANY_TYPE_LABELS[t],
}));
const MODULE_OPTIONS = Object.values(ModuleType).map((m) => ({
  value: m,
  label: MODULE_LABELS[m],
}));
const STATE_OPTIONS = BRAZILIAN_STATES.map((s) => ({ value: s, label: s }));

const defaultValues: CompanyFiltersData = {
  searchTerm: '',
  status: [CompanyStatus.ACTIVE],
  states: [],
  companyTypes: [],
  modules: [],
};

const CompanyFilters: React.FC<CompanyFiltersProps> = ({
  onFilter,
  loading = false,
  total,
}) => {
  const { filters, setFilter, clearAll, activeChips } = useFilterState(defaultValues, {
    onFilter,
    applyOnMount: true,
    live: true,
    labels: {
      status: 'Status',
      states: 'Estados',
      companyTypes: 'Tipos',
      modules: 'Módulos',
    },
    formatValue: {
      status: (v: string[]) =>
        v.map((s) => COMPANY_STATUS_LABELS[s as CompanyStatus] ?? s).join(', '),
      states: (v: string[]) => v.join(', '),
      companyTypes: (v: string[]) =>
        v.map((t) => COMPANY_TYPE_LABELS[t as CompanyType] ?? t).join(', '),
      modules: (v: string[]) => v.map((m) => MODULE_LABELS[m as ModuleType] ?? m).join(', '),
    },
  });

  return (
    <InlineFilterBar
      searchPlaceholder="Buscar por razão social, nome fantasia ou CNPJ"
      searchValue={filters.searchTerm}
      onSearchChange={(v) => setFilter('searchTerm', v)}
      resultCount={total}
      resultNoun="empresas"
      chips={activeChips}
      onClearAll={clearAll}
      loading={loading}
    >
      <InlineMultiSelect
        label="Status"
        value={filters.status}
        options={STATUS_OPTIONS}
        onChange={(v) => setFilter('status', v)}
        minWidth={140}
      />
      <InlineMultiSelect
        label="Estados"
        value={filters.states}
        options={STATE_OPTIONS}
        onChange={(v) => setFilter('states', v)}
        minWidth={130}
      />
      <InlineMultiSelect
        label="Tipos"
        value={filters.companyTypes}
        options={TYPE_OPTIONS}
        onChange={(v) => setFilter('companyTypes', v)}
        minWidth={140}
      />
      <InlineMultiSelect
        label="Módulos"
        value={filters.modules}
        options={MODULE_OPTIONS}
        onChange={(v) => setFilter('modules', v)}
        minWidth={150}
      />
    </InlineFilterBar>
  );
};

export default CompanyFilters;
